import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// color design tokens export
export const tokens = (mode) => ({
  ...(mode === "dark"
    ? {
        grey: {
          100: "#eef2f4",
          200: "#d9e2e6",
          300: "#bcc9cf",
          400: "#9faeb4",
          500: "#849199",
          600: "#68747a",
          700: "#4c575e",
          800: "#303a40",
          900: "#1a2125",
        },
        primary: {
          100: "#cce5f2",
          200: "#99cbe5",
          300: "#66b1d8",
          400: "#3387cc",
          500: "#003366",
          600: "#00294d",
          700: "#002444",
          800: "#001f33",
          900: "#001726",
        },
        greenAccent: {
          100: "#e3f9f2",
          200: "#c7f4e5",
          300: "#a3e8cf",
          400: "#7fdcb9",
          500: "#5bcfa3",
          600: "#46a481",
          700: "#35805f",
          800: "#255c3e",
          900: "#183c28",
        },
        redAccent: {
          100: "#fde4e4",
          200: "#fababa",
          300: "#f78f8f",
          400: "#f46363",
          500: "#f03737",
          600: "#c02c2c",
          700: "#902121",
          800: "#601616",
          900: "#3c0e0e",
        },
        blueAccent: {
          100: "#e6f1fb",
          200: "#cce3f7",
          300: "#99c7ef",
          400: "#66abe7",
          500: "#338fdc",
          600: "#276fba",
          700: "#1c5187",
          800: "#123354",
          900: "#0a1a2c",
        },
        sidebar: {
          main: "#187089",
          active: "#11fa47",
          hover: "#0fd23b",
        },
      }
    : {
        grey: {
          100: "#1a2125",
          200: "#303a40",
          300: "#4c575e",
          400: "#68747a",
          500: "#849199",
          600: "#9faeb4",
          700: "#bcc9cf",
          800: "#d9e2e6",
          900: "#eef2f4",
        },
        primary: {
          100: "#001726",
          200: "#001f33",
          300: "#002444",
          400: "#00294d",
          500: "#003366",
          600: "#3387cc",
          700: "#66b1d8",
          800: "#99cbe5",
          900: "#cce5f2",
        },
        greenAccent: {
          100: "#003d33",
          200: "#00594d",
          300: "#007466",
          400: "#009980",
          500: "#00bf99",
          600: "#33d1ad",
          700: "#66e3c1",
          800: "#99f5d6",
          900: "#ccffe8",
        },
        redAccent: {
          100: "#3c0e0e",
          200: "#601616",
          300: "#902121",
          400: "#c02c2c",
          500: "#f03737",
          600: "#f46363",
          700: "#f78f8f",
          800: "#fababa",
          900: "#fde4e4",
        },
        blueAccent: {
          100: "#0a1a2c",
          200: "#123354",
          300: "#1c5187",
          400: "#276fba",
          500: "#338fdc",
          600: "#66abe7",
          700: "#99c7ef",
          800: "#cce3f7",
          900: "#e6f1fb",
        },
        sidebar: {
          main: "#001726",
          active: "#11fa47",
          hover: "#0fd23b",
        },
      }),
});

// mui theme settings
export const themeSettings = (mode) => {
  const colors = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "dark"
        ? {
            primary: { main: colors.primary[500] },
            secondary: { main: colors.greenAccent[500] },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.primary[900],
            },
          }
        : {
            primary: { main: colors.primary[100] },
            secondary: { main: colors.greenAccent[500] },
            neutral: {
              dark: colors.grey[700],
              main: colors.grey[500],
              light: colors.grey[100],
            },
            background: {
              default: colors.grey[900],
            },
          }),
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            ...(mode === "dark" && {
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#187089",
              },
            }),
          },
        },
      },
    },
    typography: {
      fontFamily: ["Cabin", "sans-serif"].join(","),
      fontSize: 12,
      h1: { fontFamily: "Cabin, sans-serif", fontSize: 40 },
      h2: { fontFamily: "Cabin, sans-serif", fontSize: 32 },
      h3: { fontFamily: "Cabin, sans-serif", fontSize: 24 },
      h4: { fontFamily: "Cabin, sans-serif", fontSize: 20 },
      h5: { fontFamily: "Cabin, sans-serif", fontSize: 16 },
      h6: { fontFamily: "Cabin, sans-serif", fontSize: 14 },
    },
  };
};

// context for color mode
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});

export const useMode = () => {
  const [mode, setMode] = useState("dark");

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};